import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { receivablesService } from "../services/dataService";
import { CurrencyBalance, FirmReceivableSummary, ReceivablesOverview } from "../types";
import { formatCurrency, turkishIncludes } from "../utils/formatters";
import { usePermissions } from "../context/PermissionContext";
import PageLoader from "../components/PageLoader";
import "./Receivables.css";

const fmtDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString("tr-TR") : "—");

/** Birden çok para biriminde bakiye: "1.250,00 ₺ · 400,00 $" */
const Amounts: React.FC<{ items: CurrencyBalance[]; pick: (b: CurrencyBalance) => number; cls?: (v: number) => string }> = ({ items, pick, cls }) => {
  const shown = items.filter((b) => pick(b) !== 0);
  if (shown.length === 0) return <span className="rc-amt zero">—</span>;
  return (
    <span className="rc-amt-multi">
      {shown.map((b, i) => (
        <span key={b.currency}>
          {i > 0 && <span className="muted"> · </span>}
          <span className={`rc-amt ${cls ? cls(pick(b)) : ""}`}>{formatCurrency(pick(b), b.currency)}</span>
        </span>
      ))}
    </span>
  );
};

const AgingBar: React.FC<{ b: CurrencyBalance[] }> = ({ b }) => {
  const a = b.reduce(
    (acc, x) => ({ a0: acc.a0 + x.aging0_30, a1: acc.a1 + x.aging31_60, a2: acc.a2 + x.aging61_90, a3: acc.a3 + x.aging90Plus }),
    { a0: 0, a1: 0, a2: 0, a3: 0 },
  );
  const total = a.a0 + a.a1 + a.a2 + a.a3;
  if (total <= 0) return <span className="muted">—</span>;
  const pct = (v: number) => `${(v / total) * 100}%`;
  return (
    <div className="rc-aging" title={`0-30: ${a.a0.toFixed(2)} · 31-60: ${a.a1.toFixed(2)} · 61-90: ${a.a2.toFixed(2)} · 90+: ${a.a3.toFixed(2)}`}>
      <span className="a0" style={{ width: pct(a.a0) }} />
      <span className="a1" style={{ width: pct(a.a1) }} />
      <span className="a2" style={{ width: pct(a.a2) }} />
      <span className="a3" style={{ width: pct(a.a3) }} />
    </div>
  );
};

const Receivables: React.FC = () => {
  const navigate = useNavigate();
  const { has } = usePermissions();
  const [data, setData] = useState<ReceivablesOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [onlyOpen, setOnlyOpen] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");

  const load = async () => {
    setLoading(true); setError("");
    try { setData(await receivablesService.overview()); }
    catch (e: any) { setError(e?.response?.data?.message || "Cari özet yüklenemedi."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const syncInvoices = async () => {
    setSyncing(true); setSyncMsg("");
    try {
      const r = await receivablesService.syncInvoices();
      setSyncMsg(r.added > 0 ? `${r.added} fatura deftere işlendi.` : "Deftere işlenmemiş fatura yok.");
      await load();
    } catch { setSyncMsg("Eşitleme başarısız."); }
    finally { setSyncing(false); }
  };

  const rows = useMemo(() => {
    if (!data) return [];
    return data.firms.filter((f) => {
      if (onlyOpen && !f.balances.some((b) => b.balance !== 0)) return false;
      if (!search.trim()) return true;
      return turkishIncludes(f.firmName, search) || turkishIncludes(f.firmCode, search);
    });
  }, [data, search, onlyOpen]);

  const totalOf = (pick: (b: CurrencyBalance) => number) =>
    (data?.totals || []).filter((t) => pick(t) !== 0).map((t) => formatCurrency(pick(t), t.currency));

  if (loading && !data) return <PageLoader message="Cari hesaplar yükleniyor..." />;

  return (
    <div className="rc-page">
      <div className="rc-head">
        <div>
          <h1>Cari Hesaplar</h1>
          <p>Firma bazlı alacak bakiyesi, vadesi geçmiş tutarlar ve yaşlandırma. Sistemden kesilen faturalar otomatik işlenir.</p>
        </div>
        <div className="rc-head-actions">
          {has("receivables:update") && (
            <button className="rc-btn" onClick={syncInvoices} disabled={syncing} title="Geçmiş faturaları deftere işle (bir kez yeter, tekrarı zararsız)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-3-6.7" /><path d="M21 3v6h-6" /></svg>
              {syncing ? "Eşitleniyor…" : "Faturaları Eşitle"}
            </button>
          )}
          <button className="rc-btn" onClick={load} disabled={loading}>Yenile</button>
        </div>
      </div>

      {error && <div className="ic-alert error" style={{ marginBottom: "1rem" }}>{error}</div>}
      {syncMsg && <div className="ic-alert warn" style={{ marginBottom: "1rem" }}>{syncMsg}</div>}

      {data && (
        <div className="rc-kpis">
          <div className="rc-kpi">
            <div className="rc-kpi-label">Toplam Alacak</div>
            <div className="rc-kpi-value rc-kpi-multi">{totalOf((b) => b.balance).map((s) => <span key={s}>{s}</span>)}{totalOf((b) => b.balance).length === 0 && "—"}</div>
            <div className="rc-kpi-sub">{data.firms.filter((f) => f.balances.some((b) => b.balance > 0)).length} firma borçlu</div>
          </div>
          <div className="rc-kpi overdue">
            <div className="rc-kpi-label">Vadesi Geçmiş</div>
            <div className="rc-kpi-value rc-kpi-multi">{totalOf((b) => b.overdue).map((s) => <span key={s}>{s}</span>)}{totalOf((b) => b.overdue).length === 0 && "—"}</div>
            <div className="rc-kpi-sub">{data.firms.filter((f) => f.balances.some((b) => b.overdue > 0)).length} firmada gecikme</div>
          </div>
          <div className="rc-kpi">
            <div className="rc-kpi-label">Vadesi Gelmemiş</div>
            <div className="rc-kpi-value rc-kpi-multi">{totalOf((b) => b.notDue).map((s) => <span key={s}>{s}</span>)}{totalOf((b) => b.notDue).length === 0 && "—"}</div>
            <div className="rc-kpi-sub">Varsayılan vade {data.defaultTermDays} gün</div>
          </div>
          <div className="rc-kpi collected">
            <div className="rc-kpi-label">Son 30 Gün Tahsilat</div>
            <div className="rc-kpi-value rc-kpi-multi">
              {Object.entries(data.collectedLast30Days).map(([c, v]) => <span key={c}>{formatCurrency(v, c)}</span>)}
              {Object.keys(data.collectedLast30Days).length === 0 && "—"}
            </div>
            <div className="rc-kpi-sub">Nakit, havale, çek ve kart</div>
          </div>
        </div>
      )}

      <div className="rc-toolbar">
        <input className="rc-input rc-search" placeholder="Firma adı veya kodu ara..." value={search} onChange={(e) => setSearch(e.target.value)} />
        <label className="rc-check">
          <input type="checkbox" checked={onlyOpen} onChange={(e) => setOnlyOpen(e.target.checked)} />
          Yalnız bakiyesi olanlar
        </label>
        <div className="rc-aging-legend" style={{ marginLeft: "auto" }}>
          <span><i style={{ background: "#FEC84B" }} />0-30</span>
          <span><i style={{ background: "#F79009" }} />31-60</span>
          <span><i style={{ background: "#DC6803" }} />61-90</span>
          <span><i style={{ background: "#B42318" }} />90+</span>
        </div>
      </div>

      <div className="rc-card">
        <div className="rc-table-wrap">
          <table className="rc-table rt rt-stack">
            <thead>
              <tr>
                <th>Firma</th>
                <th className="num">Bakiye</th>
                <th className="num">Vadesi Geçmiş</th>
                <th className="rt-p3">Yaşlandırma</th>
                <th className="rt-p4 num">Açık Fatura</th>
                <th className="rt-p2">Son Fatura</th>
                <th className="rt-p2">Son Tahsilat</th>
                <th className="rt-p3">En Eski Vade</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={8}>
                  <div className="rc-empty">
                    <strong>{data?.firms.length ? "Filtreye uyan kayıt yok" : "Henüz cari hareket yok"}</strong>
                    {!data?.firms.length && "Sistemden fatura kesildikçe borçlar otomatik işlenir; geçmiş faturalar için \"Faturaları Eşitle\"."}
                  </div>
                </td></tr>
              ) : rows.map((f: FirmReceivableSummary) => (
                <tr key={f.firmId} className="clickable" onClick={() => navigate(`/receivables/firm/${f.firmId}`)}>
                  <td className="rt-main">
                    <div className="rc-firm">
                      <span className="rc-firm-name">{f.firmName}</span>
                      <span className="rc-firm-code">{f.firmCode}</span>
                    </div>
                  </td>
                  <td className="num" data-label="Bakiye">
                    <Amounts items={f.balances} pick={(b) => b.balance} cls={(v) => (v > 0 ? "pos" : v < 0 ? "neg" : "zero")} />
                  </td>
                  <td className="num" data-label="Vadesi Geçmiş">
                    <Amounts items={f.balances} pick={(b) => b.overdue} cls={() => "overdue"} />
                  </td>
                  <td className="rt-p3" data-label="Yaşlandırma"><AgingBar b={f.balances} /></td>
                  <td className="rt-p4 num" data-label="Açık Fatura">{f.openInvoiceCount || "—"}</td>
                  <td className="rt-p2" data-label="Son Fatura">{fmtDate(f.lastInvoiceDate)}</td>
                  <td className="rt-p2" data-label="Son Tahsilat">{fmtDate(f.lastPaymentDate)}</td>
                  <td className="rt-p3" data-label="En Eski Vade">
                    {f.oldestOverdueDue ? <span className="rc-chip danger">{fmtDate(f.oldestOverdueDue)}</span> : <span className="muted">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Receivables;
